import React, { useEffect, useState } from 'react';
import { getDataSourceSummary } from 'Metadata/metaAnalysisApi';
import { formatBytes } from 'Utils/fileTypeUtils';
import { graphSourceLabel } from './dataGraphApi';

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString());
const text = (v) => (v == null || v === '' ? '—' : String(v));
const stamp = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

/** schema.table, falling back to the bare name on pre-V26 snapshots. */
export const qualify = (t) => (t && t.schemaName ? `${t.schemaName}.${t.tableName}` : (t && t.tableName) || '');

/**
 * The biggest `n` tables of a profile snapshot. Ranked on size, or on rows when the engine
 * reported no sizes at all (Salesforce / some MySQL setups) — a list ordered by nothing is
 * worse than one ordered by the metric we do have.
 */
export const largestTables = (tables, n = 4) => {
  const rows = (tables || []).filter((t) => t && t.tableName);
  const key = rows.some((t) => t.sizeBytes != null) ? 'sizeBytes' : 'rowCount';
  return [...rows]
    .sort((a, b) => (Number(b[key]) || 0) - (Number(a[key]) || 0))
    .slice(0, n);
};

const Fact = ({ label, value, title }) => (
  <div className='dg-fact' title={title}>
    <span className='dg-fact__label'>{label}</span>
    <span className='dg-fact__value'>{value}</span>
  </div>
);

/**
 * Expanded row body for one source in the Data Graph list: who this source actually is
 * (fingerprint, engine + version, host / database / schemas), what the last Profile
 * captured (counts, size, when) and its four largest tables — enough to tell two
 * same-named environments apart without leaving the page. The profile summary is fetched
 * lazily, so collapsed rows cost nothing; a 404 means "never profiled".
 */
const SourceDetailPanel = ({ dataSourceId, connection, summary, fingerprint, onReview }) => {
  const [profile, setProfile] = useState(null);
  const [state, setState] = useState('loading');   // loading | ready | none | error

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await getDataSourceSummary(dataSourceId);
        if (!alive) return;
        setProfile(res || null);
        setState(res ? 'ready' : 'none');
      } catch (e) {
        if (!alive) return;
        setState(Number(e && e.status) === 404 ? 'none' : 'error');
      }
    })();
    return () => { alive = false; };
  }, [dataSourceId]);

  const conn = (profile && profile.connection) || {};
  const schemas = (profile && profile.schemaNames) || [];
  const hostLine = conn.host ? `${conn.host}${conn.port != null ? `:${conn.port}` : ''}` : null;
  const inferred = (summary && summary.inferredEdgeCounts) || {};
  const top = largestTables(profile && profile.tables);

  return (
    <div className='dg-detail' data-testid={`dg-detail-${dataSourceId}`}>
      <div className='dg-facts'>
        <section className='dg-factgroup'>
          <h4 className='dg-factgroup__title'>Identity</h4>
          <Fact label='Fingerprint'
            title='The commercial application schema this source matched when it was fingerprinted'
            value={fingerprint && fingerprint.product
              ? <>{fingerprint.product}{fingerprint.confidence != null && (
                <span className='dg-muted'> · {Math.round(fingerprint.confidence * 100)}% match</span>
              )}</>
              : 'No known product matched'} />
          <Fact label='Source name' value={text(connection && connection.name)} />
          <Fact label='Source id' value={text(dataSourceId)} />
          <Fact label='Platform type' value={text(connection && connection.datasourceType)} />
        </section>

        <section className='dg-factgroup'>
          <h4 className='dg-factgroup__title'>Engine</h4>
          <Fact label='Engine' value={text((profile && profile.engine) || (summary && summary.engine)
            || (connection && (connection.engine || connection.type)))} />
          <Fact label='Version' value={text(profile && profile.engineVersion)} />
          <Fact label='Character set' value={text(profile && profile.characterSet)} />
          <Fact label='Collation' value={text(profile && profile.collation)} />
        </section>

        <section className='dg-factgroup'>
          <h4 className='dg-factgroup__title'>Location</h4>
          <Fact label='Host' value={text(hostLine)} />
          <Fact label='Database' value={text(conn.database)} />
          <Fact label='Connection schema' value={text(conn.schema)} />
          <Fact label={`Schemas fingerprinted${schemas.length > 0 ? ` (${schemas.length})` : ''}`}
            title={schemas.join(', ')}
            value={schemas.length > 0 ? schemas.join(', ') : '—'} />
        </section>

        <section className='dg-factgroup'>
          <h4 className='dg-factgroup__title'>Latest fingerprint</h4>
          <Fact label='Taken' value={stamp((profile && profile.snapshotTakenAt) || (summary && summary.snapshotTakenAt))} />
          <Fact label='Tables' value={fmt(profile ? profile.tableCount : (summary && summary.tableCount))} />
          <Fact label='Rows' value={fmt(profile && profile.totalRowCount)} />
          <Fact label='Size' value={profile && profile.totalSizeBytes != null ? formatBytes(profile.totalSizeBytes) : '—'} />
          <Fact label='Database users' value={fmt(profile && profile.userCount)} />
        </section>

        <section className='dg-factgroup'>
          <h4 className='dg-factgroup__title'>Graph</h4>
          <Fact label='Built from' title='Where the declared relationships came from'
            value={summary ? graphSourceLabel(summary.graphSource) : '—'} />
          <Fact label='Declared keys' value={fmt(summary && summary.declaredEdgeCount)} />
          <Fact label='Connected / isolated'
            title='Tables that link to at least one other table, versus tables with no link at all'
            value={summary ? `${fmt(summary.connectedTableCount)} / ${fmt(summary.isolatedTableCount)}` : '—'} />
          <Fact label='Table groups' value={fmt(summary && summary.componentCount)} />
          {inferred.active != null && <Fact label='Inferred (active)' value={fmt(inferred.active)} />}
          {summary && summary.unresolvedManualLinkCount > 0 && (
            <Fact label='Unresolved links'
              title='Manual links whose table or column no longer exists in the latest snapshot'
              value={fmt(summary.unresolvedManualLinkCount)} />
          )}
        </section>
      </div>

      <div className='dg-detail__tables'>
        <h4 className='dg-factgroup__title'>Largest tables</h4>
        {state === 'loading' && <div className='dg-muted'>Loading fingerprint…</div>}
        {state === 'none' && <div className='dg-muted'>No fingerprint snapshot for this source yet.</div>}
        {state === 'error' && <div className='dg-error'>Could not load the fingerprint for this source.</div>}
        {state === 'ready' && top.length === 0 && <div className='dg-muted'>The latest fingerprint captured no tables.</div>}
        {state === 'ready' && top.length > 0 && (
          <div className='dg-minitable' role='table' aria-label='Largest tables'>
            <div className='dg-minitable__head' role='row'>
              <span>Table</span><span className='dg-num'>Rows</span><span className='dg-num'>Size</span><span className='dg-num'>Columns</span>
            </div>
            {top.map((t) => (
              <div className='dg-minitable__row' role='row' key={qualify(t)}>
                <span className='dg-mono' title={qualify(t)}>{qualify(t)}</span>
                <span className='dg-num'>{fmt(t.rowCount)}</span>
                <span className='dg-num'>{t.sizeBytes != null ? formatBytes(t.sizeBytes) : '—'}</span>
                <span className='dg-num'>{fmt(t.columnCount)}</span>
              </div>
            ))}
          </div>
        )}
        {summary && (
          <button type='button' className='dg-link dg-detail__review' onClick={onReview}
            data-testid={`dg-review-${dataSourceId}`}>
            Data graphs →
          </button>
        )}
      </div>
    </div>
  );
};

export default SourceDetailPanel;
